import { createRoute, createRootRoute, createRouter, Outlet, redirect } from '@tanstack/react-router';

import { Layout } from './components/Layout';
import { AlertDetail } from './routes/AlertDetail';
import { AlertsList } from './routes/AlertsList';
import { DetectionDetail } from './routes/DetectionDetail';
import { DetectionsList } from './routes/DetectionsList';
import { Events } from './routes/Events';
import { IntegrationDetail } from './routes/IntegrationDetail';
import { Integrations } from './routes/Integrations';
import { Organizations } from './routes/Organizations';
import { Overview } from './routes/Overview';
import { Preferences } from './routes/Preferences';
import { Users } from './routes/Users';
import { parseEventsSearch } from './lib/eventSearch';
import { parseAlertsSearch, parseDetectionsSearch } from './lib/listSearch';

const rootRoute = createRootRoute({
  component: () => (
    <Layout>
      <Outlet />
    </Layout>
  ),
});

const overviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: Overview,
});

/** Filters live in the URL (task 88): linkable, shareable, restored by Back. */
const alertsListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/alerts',
  validateSearch: parseAlertsSearch,
  component: AlertsList,
});

/** `$alertRef` is the BSM short id in links we mint, but a uuid is accepted too. */
const alertDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/alerts/$alertRef',
  component: function AlertDetailRoute() {
    const { alertRef } = alertDetailRoute.useParams();
    return <AlertDetail alertRef={alertRef} />;
  },
});

const detectionsListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/detections',
  validateSearch: parseDetectionsSearch,
  component: DetectionsList,
});

const detectionDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/detections/$detectionId',
  component: function DetectionDetailRoute() {
    const { detectionId } = detectionDetailRoute.useParams();
    return <DetectionDetail detectionId={detectionId} />;
  },
});

/** The selected Client's events (Story 24); the search lives in the URL (task 158). */
const eventsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/events',
  validateSearch: parseEventsSearch,
  component: Events,
});

/** MSPs and their Clients (Story 48, task 208). */
const organizationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/organizations',
  component: Organizations,
});

/** The page's name before task 208; old links and bookmarks still land. */
const clientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clients',
  beforeLoad: () => {
    throw redirect({ to: '/organizations', replace: true });
  },
});

const integrationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/integrations',
  component: Integrations,
});

/** `$name` is a connector name from the registry: `defender`, `sentinelone`, … */
const integrationDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/integrations/$name',
  component: function IntegrationDetailRoute() {
    const { name } = integrationDetailRoute.useParams();
    return <IntegrationDetail name={name} />;
  },
});

/** Users (Story 22): `manage_org`, by organization (Story 48); the page says so to anyone else. */
const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users',
  component: Users,
});

/** The signed-in user's own email preferences (Story 26): every user. */
const preferencesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/preferences',
  component: Preferences,
});

const routeTree = rootRoute.addChildren([
  overviewRoute,
  alertsListRoute,
  alertDetailRoute,
  detectionsListRoute,
  detectionDetailRoute,
  eventsRoute,
  organizationsRoute,
  clientsRoute,
  integrationsRoute,
  integrationDetailRoute,
  usersRoute,
  preferencesRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
